import React, { useEffect, useState } from "react";
import { Trash2, Heart, Wallet, RotateCcw, Film } from "lucide-react";
import StitchDialog from "@/components/StitchDialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { api, fmtUSD, fmtINR } from "@/lib/api";

const LABEL = { image: "Image", video: "Video", remix: "Remix", audio: "Audio", blackfx: "Black FX" };

export default function HistoryHeader({ projectId, count, unit, refreshKey, likedOnly, setLikedOnly, onOpenTrash, onReset, items = [], canStitch }) {
  const [spend, setSpend] = useState(null);
  const [stitch, setStitch] = useState(false);
  const [ask, setAsk] = useState(false);
  useEffect(() => { api.get(`/projects/${projectId}/spend`).then((r) => setSpend(r.data)).catch(() => {}); }, [projectId, refreshKey]);

  return (
    <div className="mb-4 space-y-3">
      <div className="ax-panel px-4 py-3 flex flex-wrap items-center gap-x-6 gap-y-2" data-testid="spend-tracker">
        <span className="flex items-center gap-2 text-xs font-mono uppercase tracking-widest text-zinc-500"><Wallet className="w-4 h-4 text-[#7AF25F]" />Project spend</span>
        <span className="text-2xl font-heading font-bold ax-gradient-text" data-testid="spend-inr">{spend ? fmtINR(spend.inr) : "…"}</span>
        <span className="text-sm font-mono text-zinc-400" data-testid="spend-usd">{spend ? fmtUSD(spend.usd) : ""}</span>
        {spend && <span className="text-xs text-zinc-500">{spend.count} paid generations</span>}
        <div className="flex flex-wrap gap-1.5 ml-auto">
          {Object.entries(spend?.by_section || {}).map(([k, v]) => (
            <span key={k} className="ax-badge" data-testid={`spend-section-${k}`}>{LABEL[k] || k} {fmtINR(v * (spend?.inr_rate || 0))} · {fmtUSD(v)}</span>
          ))}
        </div>
      </div>
      <div className="flex items-center gap-3">
        <h1 className="font-heading text-xl font-semibold">History</h1>
        <span className="text-xs text-zinc-500 font-mono">{count} {unit}</span>
        <div className="flex-1" />
        {canStitch && <button onClick={() => setStitch(true)} className="ax-chip" data-testid="history-stitch-button"><Film className="w-3.5 h-3.5" />Stitch & Export</button>}
        {onReset && <button onClick={() => setAsk(true)}
          className="ax-chip hover:!border-amber-500/50" data-testid="panel-reset-button"><RotateCcw className="w-3.5 h-3.5" />Reset panel</button>}
        <button onClick={() => setLikedOnly(!likedOnly)} data-testid="history-liked-filter"
          className={`ax-chip ${likedOnly ? "!border-pink-500/50 !text-pink-300" : ""}`}>
          <Heart className={`w-3.5 h-3.5 ${likedOnly ? "fill-pink-400 text-pink-400" : ""}`} />Liked
        </button>
        <button onClick={onOpenTrash} className="ax-chip" data-testid="history-trash-button"><Trash2 className="w-3.5 h-3.5" />Recycle Bin</button>
      </div>
      {canStitch && <StitchDialog open={stitch} onOpenChange={setStitch} items={items} />}
      <AlertDialog open={ask} onOpenChange={setAsk}>
        <AlertDialogContent className="bg-[#0E1114] border-zinc-800 text-zinc-100" data-testid="reset-confirm">
          <AlertDialogHeader>
            <AlertDialogTitle>Reset this panel?</AlertDialogTitle>
            <AlertDialogDescription className="text-zinc-400">Everything you filled in here is cleared so you can start fresh. Your history and files stay.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="bg-transparent border-zinc-700 text-zinc-300" data-testid="reset-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={() => onReset()} className="bg-amber-500 text-black hover:bg-amber-400" data-testid="reset-confirm-button">Reset</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
